/**
 * Static chapter spec — mirrors `backend/routes_inner_mirror.CHAPTERS`.
 * Keep the two in sync; the backend is the source of truth.
 */
export const CHAPTERS = [
  {
    n: 1,
    eyebrow: "01 / 07 — THE ARRIVAL",
    title: "What brought you to KALKI?",
    sub: "Every journey begins with something we are searching for.",
    reflection: "Every question begins a journey of understanding.",
    options: [
      { k: "curiosity",          icon: "✨", label: "Curiosity" },
      { k: "guidance",           icon: "🧭", label: "Guidance" },
      { k: "peace",              icon: "🪷", label: "Peace" },
      { k: "astrology",          icon: "🪐", label: "Astrology" },
      { k: "something_personal", icon: "❤️", label: "Something Personal" },
    ],
  },
  {
    n: 2,
    eyebrow: "02 / 07 — THE FEELING",
    title: "How did KALKI leave you feeling?",
    sub: "There is no right answer. Only your experience.",
    reflection: "Every feeling teaches us something, including the ones we did not expect.",
    options: [
      { k: "inspired",  icon: "🌟", label: "Inspired" },
      { k: "peaceful",  icon: "🕊️", label: "Peaceful" },
      { k: "curious",   icon: "🧭", label: "Curious" },
      { k: "confused",  icon: "🌀", label: "Confused" },
      { k: "unchanged", icon: "·",  label: "Unchanged" },
    ],
  },
  {
    n: 3,
    eyebrow: "03 / 07 — THE SPARK",
    title: "What made you stop and think?",
    sub: "Which part of your KALKI experience stayed with you?",
    reflection: "Understanding begins when something makes us see life differently.",
    options: [
      { k: "talking",      icon: "💬", label: "Talking to KALKI" },
      { k: "future",       icon: "🔭", label: "My Future Timeline" },
      { k: "chart",        icon: "✨", label: "My Birth Chart" },
      { k: "karma_dharma", icon: "🪔", label: "Karma & Dharma" },
      { k: "nothing_yet",  icon: "·",  label: "Nothing Yet" },
    ],
  },
  {
    n: 4,
    eyebrow: "04 / 07 — THE UNFINISHED MOMENT",
    title: "Was there a moment KALKI lost you?",
    sub: "Help us understand where your journey could have been better.",
    reflection: "Honest reflection is the beginning of meaningful improvement.",
    allowText: true,
    textPlaceholder: "Tell us what happened…",
    textMax: 300,
    options: [
      { k: "not_understood", icon: "·", label: "KALKI didn't understand me" },
      { k: "generic",        icon: "·", label: "The answer felt generic" },
      { k: "slow",           icon: "·", label: "The experience was too slow" },
      { k: "difficult",      icon: "·", label: "Something was difficult to use" },
      { k: "flowed_well",    icon: "✓", label: "Everything flowed well" },
    ],
  },
  {
    n: 5,
    eyebrow: "05 / 07 — THE WISH",
    title: "If KALKI could do one more thing for you…",
    sub: "What would make KALKI more meaningful in your life?",
    reflection: "Every meaningful creation begins with someone's wish for something better.",
    allowText: true,
    textPlaceholder: "Something else…",
    textMax: 300,
    options: [
      { k: "understand_me",        icon: "🧠", label: "Understand me better" },
      { k: "reliable_predictions", icon: "🪐", label: "Give more reliable predictions" },
      { k: "take_action",          icon: "⚡", label: "Help me take action" },
      { k: "find_peace",           icon: "🕊️", label: "Help me find peace" },
      { k: "connect_people",       icon: "🤝", label: "Connect me with people" },
      { k: "something_else",       icon: "·",  label: "Something else" },
    ],
  },
  {
    n: 6,
    eyebrow: "06 / 07 — THE CONNECTION",
    title: "Who would you bring on this journey?",
    sub: "Sometimes the most thoughtful invitation is simply remembering someone.",
    reflection: "Connection becomes meaningful when it is offered with care.",
    options: [
      { k: "partner",  icon: "❤️", label: "My Partner" },
      { k: "family",   icon: "🏠", label: "My Family" },
      { k: "friend",   icon: "🤝", label: "My Friend" },
      { k: "seeker",   icon: "🧭", label: "Someone Seeking Guidance" },
      { k: "just_me",  icon: "·",  label: "Just Me, For Now" },
    ],
  },
  {
    n: 7,
    eyebrow: "07 / 07 — THE SANKALP",
    title: "What should KALKI become for you?",
    sub: "Leave one thought, one wish or one idea. Inspired by the Bhagavad Gita's teachings on Karma Yoga.",
    reflection: "Meaningful change begins with thoughtful action.",
    allowText: true,
    isSankalp: true,
    textPlaceholder: "I wish KALKI could…",
    textMax: 500,
  },
];

export const INVITE_SLOTS = [
  { slot: 1, label: "Someone Special" },
  { slot: 2, label: "A Friend" },
  { slot: 3, label: "Family" },
  { slot: 4, label: "Someone Seeking Guidance" },
  { slot: 5, label: "Someone You Care About" },
];

/**
 * KALKI Inner Mirror 1.3 + 1.4 — five relationship-based invitation slots.
 * `icon` is a single emoji for the slot CTA; `theme` is a short tag used in
 * the live preview; `message(firstName, recipient)` builds the editable
 * default WhatsApp copy. ALWAYS include the KALKI homepage link.
 */
const KALKI_HOME = "https://www.superlife.living/";
export const RELATIONSHIP_SLOTS = [
  {
    slot: 1, key: "love",     icon: "❤️", label: "My Love / Partner",
    theme: "Togetherness",
    message: (name, who) => `${who ? who + ", " : ""}Hey, I've started a journey towards inner fulfillment with KALKI. ❤️
And it reminded me that the most beautiful journeys are the ones we share with someone special.
I thought of you.
Discover your inner light with KALKI. 🕉️
${KALKI_HOME}`,
  },
  {
    slot: 2, key: "friend",   icon: "🫶", label: "My Best Friend",
    theme: "Gratitude",
    message: (name, who) => `${who ? who + ", " : ""}Hey, I've started exploring my inner self with KALKI. ✨
Life is better when we grow, learn and discover ourselves together.
You're one of the first people I wanted to share this with!
${KALKI_HOME}`,
  },
  {
    slot: 3, key: "family",   icon: "🪷", label: "My Family",
    theme: "Blessings",
    message: (name, who) => `${who ? who + ", " : ""}I've begun a little journey towards inner peace and fulfillment with KALKI. 🙏
Our values, Karma and Dharma shape the life we create.
Sharing this with love and gratitude.
${KALKI_HOME}`,
  },
  {
    slot: 4, key: "special",  icon: "✨", label: "Someone Special",
    theme: "Appreciation",
    message: (name, who) => `${who ? who + ", " : ""}Hey, I've started a meaningful journey of self-discovery with KALKI.
Sometimes a little reflection helps us understand what truly matters.
You came to mind, so I wanted to share my journey with you. ❤️
${KALKI_HOME}`,
  },
  {
    slot: 5, key: "seeker",   icon: "☀️", label: "Someone Seeking Light",
    theme: "Hope",
    message: (name, who) => `${who ? who + ", " : ""}Hey, I've started a journey towards inner fulfillment with KALKI. ☀️
Sometimes all we need is a moment to reconnect with ourselves.
I thought this might bring a little inspiration to your day.
${KALKI_HOME}`,
  },
];

const POSITIVE_MOTIVATIONS = { curiosity: "Curiosity", guidance: "Guidance",
  peace: "Peace", astrology: "Astrology", something_personal: "Something personal" };
const FEELING_LABEL = { inspired: "Inspired", peaceful: "Peaceful",
  curious: "Curious", confused: "Confused", unchanged: "Unchanged" };
const MEMORY_LABEL = { talking: "Talking to KALKI", future: "The Future Timeline",
  chart: "The Birth Chart", karma_dharma: "Karma & Dharma", nothing_yet: "Nothing yet" };
const WISH_LABEL = { understand_me: "Understand me better",
  reliable_predictions: "More reliable predictions",
  take_action: "Help me take action", find_peace: "Help me find peace",
  connect_people: "Connect me with people", something_else: "Something new" };

export const labels = { POSITIVE_MOTIVATIONS, FEELING_LABEL, MEMORY_LABEL, WISH_LABEL };

/** Build the invitation message based on sentiment. */
export function buildInviteCopy({ firstName, feeling, friction, link }) {
  const rough = ["confused", "unchanged"].includes(feeling) ||
                ["not_understood", "generic", "slow", "difficult"].includes(friction);
  if (rough) {
    return `I've been exploring KALKI and sharing honest feedback to help improve it. Thought you might like to explore it too.\n\n${link}`;
  }
  const named = firstName ? ` (from ${firstName})` : "";
  return `🕉️ I just completed my Inner Mirror journey with KALKI${named}.
It made me reflect on what I seek, what matters to me and what I want from life.
I thought of you. ❤️

Take a moment for yourself with KALKI.
${link}`;
}
